// ============================================================
// Client change logger
// Diffs old vs new field values for important inventory fields
// and writes rows to client_changes_log for the history view.
// ============================================================
import { supabase } from './supabase'

// Fields we care about logging changes for. Add to this list to track more.
const TRACKED_FIELDS = [
  'account_director_id',
  'account_manager_id',
  'package_label',
  'has_website',
  'has_ads',
  'has_seo',
  'has_social',
  'has_newsletters',
  'has_blogs',
  'social_posts_per_month',
  'blogs_per_month',
  'newsletters_per_month',
  'hosting_platform',
  'website_mode',
  'is_onboarding',
  'onboarding_complete',
]

// Format a value for display in the log (booleans, null, etc.)
const fmt = (v) => {
  if (v === null || v === undefined || v === '') return ''
  if (v === true) return 'yes'
  if (v === false) return 'no'
  return String(v)
}

/**
 * Diffs oldClient (the full client row before save) vs newValues (the partial update payload)
 * and inserts one row per changed tracked field into client_changes_log.
 *
 * @param {string} clientId
 * @param {object} oldClient - the full client row before save
 * @param {object} newValues - the partial update payload that was just saved
 * @param {string} currentUserId
 */
export async function logClientChanges(clientId, oldClient, newValues, currentUserId) {
  if (!oldClient || !newValues) return
  const logs = []
  for (const field of TRACKED_FIELDS) {
    if (!(field in newValues)) continue
    const oldVal = oldClient[field]
    const newVal = newValues[field]
    // Normalize empty/null/undefined as equivalent
    const normOld = (oldVal === null || oldVal === undefined || oldVal === '') ? null : oldVal
    const normNew = (newVal === null || newVal === undefined || newVal === '') ? null : newVal
    if (normOld === normNew) continue
    logs.push({
      client_id: clientId,
      changed_by: currentUserId || null,
      field_name: field,
      old_value: fmt(normOld),
      new_value: fmt(normNew),
    })
  }
  if (logs.length === 0) return
  // Insert without awaiting deeply — fire & forget but with error logging
  const { error } = await supabase.from('client_changes_log').insert(logs)
  if (error) console.error('client_changes_log insert error:', error)
}

// Human-readable labels for field_name (used in history view)
export const FIELD_LABELS = {
  account_director_id: 'Account Director',
  account_manager_id: 'Account Manager',
  package_label: 'Package Label',
  has_website: 'Website service',
  has_ads: 'Ads service',
  has_seo: 'SEO service',
  has_social: 'Social service',
  has_newsletters: 'Newsletters service',
  has_blogs: 'Blogs service',
  social_posts_per_month: 'Social posts/mo',
  blogs_per_month: 'Blogs/mo',
  newsletters_per_month: 'Newsletters/mo',
  hosting_platform: 'Hosting platform',
  website_mode: 'Website mode',
  is_onboarding: 'Onboarding flag',
  onboarding_complete: 'Onboarding complete',
}